<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'

import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import { usePagination } from '@/composables/usePagination'
import { useSearch } from '@/composables/useSearch'
import {
  getCirclePostPage,
  getCirclePostDetail,
  publishCirclePost,
  deleteCirclePost,
  onlineCirclePost,
  updateCirclePostTop,
  approveCirclePost,
  rejectCirclePost
} from '@/api/circle'

import CirclePublishModal from './components/CirclePublishModal.vue'
import CircleDetailModal from './components/CircleDetailModal.vue'
import CircleTopModal from './components/CircleTopModal.vue'
import CircleRejectModal from './components/CircleRejectModal.vue'

// 数据
const loading = ref(false)
const postList = ref([])

// 分页
const { pagination, goToPage, setPageSize, updatePagination, getPaginationParams } = usePagination({ pageSize: 10 })

// 搜索
const { searchForm, resetSearch: _resetSearch, getSearchParams } = useSearch({
  initialForm: {
    source: '',
    contentType: '',
    status: '',
    keyword: ''
  },
  onSearch: fetchData
})

// 快捷筛选
const activeQuickFilter = ref('')

const quickFilters = [
  { key: '', label: '全部' },
  { key: 'pending_user', label: '待审核' },
  { key: 'platform', label: 'B端发布' }
]

const handleQuickFilter = (key) => {
  activeQuickFilter.value = key
  if (key === 'pending_user') {
    searchForm.source = 0
    searchForm.status = 0
  } else if (key === 'platform') {
    searchForm.source = 1
    searchForm.status = ''
  } else {
    searchForm.source = ''
    searchForm.status = ''
  }
  searchForm.contentType = ''
  searchForm.keyword = ''
  pagination.current = 1
  fetchData()
}

const resetSearch = () => {
  activeQuickFilter.value = ''
  _resetSearch()
}

// 弹窗状态
const showPublishModal = ref(false)
const publishModalRef = ref()
const showDetailModal = ref(false)
const detailData = ref(null)
const showTopModal = ref(false)
const topModalRef = ref()
const topTargetPost = ref(null)
const showRejectModal = ref(false)
const rejectModalRef = ref()
const rejectTargetId = ref(null)

// 来源选项
const sourceOptions = [
  { label: '全部来源', value: '' },
  { label: '用户', value: 0 },
  { label: '平台', value: 1 }
]

// 内容类型选项
const contentTypeOptions = [
  { label: '全部类型', value: '' },
  { label: '图文', value: 1 },
  { label: '短视频', value: 2 }
]

// 状态选项
const statusOptions = [
  { label: '全部状态', value: '' },
  { label: '待审核', value: 0 },
  { label: '已通过', value: 1 },
  { label: '已拒绝', value: 2 },
  { label: '已下架', value: 3 }
]

// 状态配置
const statusConfig = {
  0: { label: '待审核', dot: 'bg-amber-400', bg: 'bg-amber-50', text: 'text-amber-700' },
  1: { label: '已通过', dot: 'bg-green-500', bg: 'bg-green-50', text: 'text-green-700' },
  2: { label: '已拒绝', dot: 'bg-red-400', bg: 'bg-red-50', text: 'text-red-700' },
  3: { label: '已下架', dot: 'bg-slate-400', bg: 'bg-slate-100', text: 'text-slate-700' }
}

// 获取数据
async function fetchData() {
  loading.value = true
  try {
    const params = {
      ...getSearchParams(),
      ...getPaginationParams()
    }
    const data = await getCirclePostPage(params)
    postList.value = data.records || []
    updatePagination(data)
  } catch (error) {
    ElMessage.error('加载动态列表失败')
    console.error('加载动态列表失败:', error)
  } finally {
    loading.value = false
  }
}

// 筛选变更
const handleFilterChange = () => {
  activeQuickFilter.value = ''
  pagination.current = 1
  fetchData()
}

// 搜索
const handleKeywordSearch = () => {
  activeQuickFilter.value = ''
  pagination.current = 1
  fetchData()
}

// 换页
const changePage = (page) => {
  goToPage(page)
  fetchData()
}

// 切换每页条数
const changePageSize = (size) => {
  setPageSize(size)
  fetchData()
}

// 格式化时间
const formatTime = (time) => {
  if (!time) return '-'
  return time.replace('T', ' ').substring(0, 16)
}

// 获取首张图片
const getFirstImage = (images) => {
  if (!images) return ''
  return images.split(',')[0]?.trim() || ''
}

// 获取图片数量
const getImageCount = (images) => {
  if (!images) return 0
  return images.split(',').filter(url => url.trim()).length
}

// ========== 操作 ==========

// 发布动态
const handlePublish = () => {
  showPublishModal.value = true
}

const handlePublishConfirm = async (data) => {
  try {
    await publishCirclePost(data)
    ElMessage.success('发布成功')
    publishModalRef.value?.done()
    fetchData()
  } catch (error) {
    ElMessage.error(error.message || '发布失败')
    publishModalRef.value?.done()
  }
}

// 查看详情
const handleViewDetail = async (item) => {
  try {
    const detail = await getCirclePostDetail(item.id)
    detailData.value = detail
    showDetailModal.value = true
  } catch (error) {
    ElMessage.error('获取详情失败')
  }
}

// 审核通过
const handleApprove = async (item) => {
  try {
    await ElMessageBox.confirm('确定审核通过该动态吗？', '确认审核', {
      confirmButtonText: '通过',
      cancelButtonText: '取消',
      type: 'info'
    })
    await approveCirclePost(item.id)
    ElMessage.success('审核通过')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.message || '操作失败')
    }
  }
}

// 审核拒绝
const handleReject = (item) => {
  rejectTargetId.value = item.id
  showRejectModal.value = true
}

const handleRejectConfirm = async (data) => {
  try {
    await rejectCirclePost(data.id, data)
    ElMessage.success('已拒绝')
    rejectModalRef.value?.done()
    fetchData()
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
    rejectModalRef.value?.done()
  }
}

// 置顶操作
const handleTop = (item) => {
  topTargetPost.value = item
  showTopModal.value = true
}

const handleTopConfirm = async (data) => {
  try {
    await updateCirclePostTop(data.id, data)
    ElMessage.success(data.isTop === 1 ? '已置顶' : '已取消置顶')
    topModalRef.value?.done()
    fetchData()
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
    topModalRef.value?.done()
  }
}

// 重新上架
const handleOnline = async (item) => {
  try {
    await ElMessageBox.confirm('确定要重新上架该动态吗？', '确认操作', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    await onlineCirclePost(item.id)
    ElMessage.success('已重新上架')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.message || '操作失败')
    }
  }
}

// 删除
const handleDelete = async (item) => {
  try {
    await ElMessageBox.confirm('确定要删除该动态吗？删除后无法恢复。', '确认删除', {
      confirmButtonText: '确认删除',
      cancelButtonText: '取消',
      type: 'warning'
    })
    await deleteCirclePost(item.id)
    ElMessage.success('删除成功')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.message || '删除失败')
    }
  }
}

onMounted(() => {
  fetchData()
})
</script>

<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">企业圈管理</h1>
            <p class="list-page-description">管理企业圈动态内容，包括审核、置顶、发布等操作</p>
          </div>
          <button
            v-permission="'app:circle:publish'"
            @click="handlePublish"
            class="btn-primary inline-flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            发布动态
          </button>
        </div>
      </div>
    </template>

    <!-- 快捷筛选 -->
    <div class="flex items-center gap-2 mb-4">
      <button
        v-for="item in quickFilters"
        :key="item.key"
        @click="handleQuickFilter(item.key)"
        :class="[
          'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer',
          activeQuickFilter === item.key
            ? 'bg-primary-600 text-white'
            : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
        ]"
      >
        {{ item.label }}
      </button>
    </div>

    <!-- 筛选栏 -->
    <PageFilterBar
      :show-refresh="true"
      :refresh-loading="loading"
      @refresh="fetchData"
    >
        <!-- 来源筛选 -->
        <select
          v-model="searchForm.source"
          class="form-select-native form-control-sm"
          @change="handleFilterChange"
        >
          <option v-for="opt in sourceOptions" :key="String(opt.value)" :value="opt.value">{{ opt.label }}</option>
        </select>

        <!-- 内容类型筛选 -->
        <select
          v-model="searchForm.contentType"
          class="form-select-native form-control-sm"
          @change="handleFilterChange"
        >
          <option v-for="opt in contentTypeOptions" :key="String(opt.value)" :value="opt.value">{{ opt.label }}</option>
        </select>

        <!-- 状态筛选 -->
        <select
          v-model="searchForm.status"
          class="form-select-native form-control-sm"
          @change="handleFilterChange"
        >
          <option v-for="opt in statusOptions" :key="String(opt.value)" :value="opt.value">{{ opt.label }}</option>
        </select>

        <!-- 关键词搜索 -->
        <div class="relative">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input
            v-model="searchForm.keyword"
            type="text"
            placeholder="搜索动态内容"
            class="form-search-input form-control-sm w-56"
            @keyup.enter="handleKeywordSearch"
          />
        </div>

      <template #searchActions>
        <button
          @click="handleKeywordSearch"
          class="btn-primary"
        >
          搜索
        </button>
        <button
          @click="resetSearch"
          class="btn-secondary"
        >
          重置
        </button>
      </template>
    </PageFilterBar>

    <!-- 表格 -->
    <div class="list-page-table-panel">
      <div class="data-table-shell">
        <div class="data-table-scroll">
          <table class="data-table text-sm">
            <thead class="data-table-head">
              <tr>
                <th class="data-table-th text-left">动态内容</th>
                <th class="data-table-th text-left w-32">发布者</th>
                <th class="data-table-th text-left w-20">类型</th>
                <th class="data-table-th text-left w-20">来源</th>
                <th class="data-table-th text-left w-20">状态</th>
                <th class="data-table-th text-left w-32">互动数据</th>
                <th class="data-table-th text-left w-36">发布时间</th>
                <th class="data-table-th text-center w-40">操作</th>
              </tr>
            </thead>
            <tbody>
              <!-- 加载骨架 -->
              <template v-if="loading">
                <tr v-for="n in 5" :key="n" class="animate-pulse">
                  <td class="data-table-td"><div class="flex gap-3"><div class="h-12 w-12 bg-slate-200 rounded"></div><div class="flex-1"><div class="h-4 bg-slate-200 rounded w-3/4 mb-2"></div><div class="h-3 bg-slate-200 rounded w-1/2"></div></div></div></td>
                  <td class="data-table-td"><div class="h-4 bg-slate-200 rounded w-16"></div></td>
                  <td class="data-table-td is-center"><div class="h-5 bg-slate-200 rounded w-12"></div></td>
                  <td class="data-table-td is-center"><div class="h-5 bg-slate-200 rounded w-14"></div></td>
                  <td class="data-table-td is-center"><div class="h-5 bg-slate-200 rounded w-14"></div></td>
                  <td class="data-table-td"><div class="h-4 bg-slate-200 rounded w-24"></div></td>
                  <td class="data-table-td"><div class="h-4 bg-slate-200 rounded w-28"></div></td>
                  <td class="data-table-td is-center"><div class="h-4 bg-slate-200 rounded w-24"></div></td>
                </tr>
              </template>

              <!-- 空状态 -->
              <tr v-if="!loading && postList.length === 0">
                <td colspan="8" class="data-table-empty">
                  <div class="flex flex-col items-center">
                    <div class="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                      <svg class="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
                      </svg>
                    </div>
                    <p class="text-slate-500 text-sm mb-1">暂无动态数据</p>
                    <p class="text-slate-400 text-xs">点击右上方按钮发布新动态</p>
                  </div>
                </td>
              </tr>

              <!-- 数据行 -->
              <template v-if="!loading">
                <tr v-for="item in postList" :key="item.id" class="data-table-row">
                  <!-- 动态内容 -->
                  <td class="data-table-td">
                    <div class="flex items-start gap-3">
                      <!-- 缩略图 -->
                      <div class="shrink-0">
                        <!-- 图文缩略图 -->
                        <template v-if="item.contentType === 1">
                          <div v-if="getFirstImage(item.images)" class="relative">
                            <img
                              :src="getFirstImage(item.images)"
                              class="h-12 w-12 object-cover rounded-lg border border-slate-200"
                            />
                            <span v-if="getImageCount(item.images) > 1" class="absolute -bottom-1 -right-1 bg-black/60 text-white text-[10px] px-1 rounded">
                              {{ getImageCount(item.images) }}图
                            </span>
                          </div>
                          <div v-else class="h-12 w-12 bg-slate-100 rounded-lg border border-slate-200 flex items-center justify-center">
                            <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                            </svg>
                          </div>
                        </template>
                        <!-- 短视频封面 -->
                        <template v-else>
                          <div class="relative">
                            <img
                              v-if="item.videoCover"
                              :src="item.videoCover"
                              class="h-12 w-12 object-cover rounded-lg border border-slate-200"
                            />
                            <div v-else class="h-12 w-12 bg-slate-900 rounded-lg flex items-center justify-center">
                              <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </div>
                            <span class="absolute bottom-0.5 right-0.5 bg-black/60 text-white flex items-center justify-center rounded w-4 h-4">
                              <svg class="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </span>
                          </div>
                        </template>
                      </div>
                      <!-- 文字内容 -->
                      <div class="flex-1 min-w-0">
                        <p class="text-sm text-slate-900 line-clamp-2">{{ item.content || '（无文字内容）' }}</p>
                        <div class="flex items-center gap-2 mt-1">
                          <span v-if="item.isTop === 1" class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-50 text-orange-600 border border-orange-200">
                            置顶
                          </span>
                          <span v-if="item.linkUrl" class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-200">
                            含链接
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>

                  <!-- 发布者 -->
                  <td class="data-table-td">
                    <div class="flex items-center gap-2">
                      <img
                        v-if="item.userAvatar"
                        :src="item.userAvatar"
                        class="w-6 h-6 rounded-full object-cover border border-slate-200"
                      />
                      <div v-else class="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                        <svg class="w-3 h-3 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                        </svg>
                      </div>
                      <span class="text-sm text-slate-700 truncate">{{ item.userNickname || '-' }}</span>
                    </div>
                  </td>

                  <!-- 类型 -->
                  <td class="data-table-td">
                    <span
                      :class="[
                        'status-badge',
                        item.contentType === 1 ? 'bg-purple-50 text-purple-700' : 'bg-cyan-50 text-cyan-700'
                      ]"
                    >
                      {{ item.contentType === 1 ? '图文' : '短视频' }}
                    </span>
                  </td>

                  <!-- 来源 -->
                  <td class="data-table-td">
                    <span
                      :class="[
                        'status-badge',
                        item.source === 0 ? 'bg-blue-50 text-blue-700' : 'bg-indigo-50 text-indigo-700'
                      ]"
                    >
                      {{ item.source === 0 ? '用户' : '平台' }}
                    </span>
                  </td>

                  <!-- 状态 -->
                  <td class="data-table-td">
                    <span
                      class="status-badge"
                      :class="[statusConfig[item.status]?.bg, statusConfig[item.status]?.text]"
                    >
                      <span class="w-1.5 h-1.5 rounded-full mr-1.5" :class="statusConfig[item.status]?.dot"></span>
                      {{ statusConfig[item.status]?.label || '未知' }}
                    </span>
                  </td>

                  <!-- 互动数据 -->
                  <td class="data-table-td">
                    <div class="flex items-center gap-3 text-xs text-slate-500">
                      <span class="flex items-center gap-0.5" title="浏览量">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                        </svg>
                        {{ item.viewCount || 0 }}
                      </span>
                      <span class="flex items-center gap-0.5" title="点赞数">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                        </svg>
                        {{ item.likeCount || 0 }}
                      </span>
                      <span class="flex items-center gap-0.5" title="收藏数">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
                        </svg>
                        {{ item.favoriteCount || 0 }}
                      </span>
                    </div>
                  </td>

                  <!-- 发布时间 -->
                  <td class="data-table-td text-slate-500">
                    {{ formatTime(item.createTime) }}
                  </td>

                  <!-- 操作 -->
                  <td class="data-table-td is-center">
                    <div class="flex items-center justify-center gap-1">
                      <!-- 查看详情 -->
                      <button
                        v-permission="'app:circle:list'"
                        @click="handleViewDetail(item)"
                        class="table-action-button is-primary"
                        title="查看详情"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                        </svg>
                      </button>

                      <!-- 审核通过（待审核状态） -->
                      <button
                        v-permission="'app:circle:audit'"
                        v-if="item.status === 0"
                        @click="handleApprove(item)"
                        class="table-action-button is-success"
                        title="审核通过"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                        </svg>
                      </button>

                      <!-- 审核拒绝（待审核状态） -->
                      <button
                        v-permission="'app:circle:audit'"
                        v-if="item.status === 0"
                        @click="handleReject(item)"
                        class="table-action-button is-danger"
                        title="审核拒绝"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>

                      <!-- 置顶/取消置顶（已通过状态） -->
                      <button
                        v-permission="'app:circle:edit'"
                        v-if="item.status === 1"
                        @click="handleTop(item)"
                        class="table-action-button is-teal"
                        :title="item.isTop === 1 ? '取消置顶' : '设置置顶'"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/>
                        </svg>
                      </button>

                      <!-- 重新上架（已下架状态） -->
                      <button
                        v-permission="'app:circle:edit'"
                        v-if="item.status === 3"
                        @click="handleOnline(item)"
                        class="table-action-button is-success"
                        title="重新上架"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                        </svg>
                      </button>

                      <!-- 删除 -->
                      <button
                        v-permission="'app:circle:delete'"
                        @click="handleDelete(item)"
                        class="table-action-button is-danger"
                        title="删除"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 分页 -->
      <AppPagination
        v-if="pagination.total > 0"
        class="pagination-bridge"
        :pagination="pagination"
        :page-sizes="[10, 20, 50]"
        @page-change="changePage"
        @size-change="changePageSize"
      />
    </div>

    <!-- 发布弹窗 -->
    <CirclePublishModal
      ref="publishModalRef"
      v-model:show="showPublishModal"
      @confirm="handlePublishConfirm"
    />

    <!-- 详情弹窗 -->
    <CircleDetailModal
      v-model:show="showDetailModal"
      :post-data="detailData"
    />

    <!-- 置顶弹窗 -->
    <CircleTopModal
      ref="topModalRef"
      v-model:show="showTopModal"
      :post-data="topTargetPost"
      @confirm="handleTopConfirm"
    />

    <!-- 拒绝弹窗 -->
    <CircleRejectModal
      ref="rejectModalRef"
      v-model:show="showRejectModal"
      :post-id="rejectTargetId"
      @confirm="handleRejectConfirm"
    />
  </ListPageShell>
</template>
